import { useForm, useFieldArray } from "react-hook-form";
// import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { Label } from "@/components/ui/label";

const formSchema = z.object({
  hourly_1: z.string().min(1, "Hourly rate 1 is required"),
  hourly_2: z.string().min(1, "Hourly rate 2 is required"),
  hourly_3: z.string().min(1, "Hourly rate 3 is required"),
  hourly_4: z.string().min(1, "Hourly rate 4 is required"),
  hourly_extra: z.string().min(1, "Hourly rate extra is required"),
  lead_notif_emails: z.array(z.string().email("Invalid email")),
  booked_notif_emails: z.array(z.string().email("Invalid email")),
  booked_calendar_emails: z.array(z.string().email("Invalid email")),
  google_reviews_link: z.string().url("Invalid URL").optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function BasicConfiguration() {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();

  const form = useForm<FormValues>({
    // resolver: zodResolver(formSchema),
    defaultValues: {
      hourly_1: selectedMover?.mover_configuration.hourly_1?.toString(),
      hourly_2: selectedMover?.mover_configuration.hourly_2?.toString(),
      hourly_3: selectedMover?.mover_configuration.hourly_3?.toString(),
      hourly_4: selectedMover?.mover_configuration.hourly_4?.toString(),
      hourly_extra: selectedMover?.mover_configuration.hourly_extra?.toString(),
      lead_notif_emails: selectedMover?.mover_configuration.lead_notif_emails,
      booked_notif_emails:
        selectedMover?.mover_configuration.booked_notif_emails,
      booked_calendar_emails:
        selectedMover?.mover_configuration.booked_calendar_emails,
      google_reviews_link:
        selectedMover?.mover_configuration.google_reviews_link || "",
    },
  });

  const {
    fields: leadNotifFields,
    append: appendLeadNotif,
    remove: removeLeadNotif,
  } = useFieldArray({
    control: form.control,
    name: "lead_notif_emails" as never,
  });

  const {
    fields: bookedNotifFields,
    append: appendBookedNotif,
    remove: removeBookedNotif,
  } = useFieldArray({
    control: form.control,
    name: "booked_notif_emails" as never,
  });

  const {
    fields: bookedCalendarFields,
    append: appendBookedCalendar,
    remove: removeBookedCalendar,
  } = useFieldArray({
    control: form.control,
    name: "booked_calendar_emails" as never,
  });

  async function onSubmit(data: FormValues) {
    try {
      const updatedMover = await updateMoverApi({
        data: {
          mover_configuration_attributes: {
            ...selectedMover?.mover_configuration,
            ...data,
            hourly_1: parseFloat(data.hourly_1),
            hourly_2: parseFloat(data.hourly_2),
            hourly_3: parseFloat(data.hourly_3),
            hourly_4: parseFloat(data.hourly_4),
            hourly_extra: parseFloat(data.hourly_extra),
          },
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Mover configuration updated",
        description:
          "The mover's basic configuration has been successfully updated.",
      });
    } catch (error) {
      console.error("Error updating mover:", error);
      toast({
        title: "Error",
        description:
          "There was an error updating the mover configuration. Please try again.",
        variant: "destructive",
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="w-full lg:w-2/3 px-4 py-4 flex flex-col justify-start items-start gap-4">
          <h2 className="text-lg font-bold">Basic Configuration</h2>
          {/* Lead Notification Emails */}
          <div>
            <FormLabel>Setup your lead and booked move notifications</FormLabel>
            {/* helper text */}
            <p className="text-sm text-gray-500"></p>
            <div className="flex flex-col gap-4 mt-2">
              <div>
                <Label>Web form lead notifications</Label>
                <div className="flex flex-col gap-2 mt-1.5">
                  {leadNotifFields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={form.control}
                      name={`lead_notif_emails.${index}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Input {...field} type="email" />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeLeadNotif(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendLeadNotif("")}
                  >
                    Add Email
                  </Button>
                </div>
              </div>

              <div>
                <Label>Booked move notifications</Label>
                <div className="flex flex-col gap-2 mt-1.5">
                  {bookedNotifFields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={form.control}
                      name={`booked_notif_emails.${index}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Input {...field} type="email" />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeBookedNotif(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendBookedNotif("")}
                  >
                    Add Email
                  </Button>
                </div>
              </div>

              <div>
                <Label>Booked move calendar invitation</Label>
                <div className="flex flex-col gap-2 mt-1.5">
                  {bookedCalendarFields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={form.control}
                      name={`booked_calendar_emails.${index}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Input {...field} type="email" />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeBookedCalendar(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendBookedCalendar("")}
                  >
                    Add Email
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <FormField
            control={form.control}
            name="google_reviews_link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Google Reviews Link (optional)</FormLabel>
                <FormControl>
                  <Input {...field} type="string" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit(onSubmit)();
            }}
            type="button"
            disabled={isLoading}
          >
            Update Mover Configuration
          </Button>
        </div>
      </form>
    </Form>
  );
}
